import React, { useEffect, useMemo, useRef } from "react";

/**
 * AngelSparks — celebration burst + drifting motes shown briefly after an
 * Angel confirmation. Self-cleans after `durationMs`.
 *
 * Visual layers (rendered as a fixed-position overlay so it sits above the
 * confirm button regardless of scroll):
 *   1) BURST particles — 28 tiny gold dots radiating from a focal point
 *      (defaults to viewport centre; can be aimed by passing origin={x,y})
 *      with randomised distance + angle. CSS keyframe drives outward
 *      translation + fade-to-zero in ~1.5s.
 *   2) DRIFT motes — 14 slower particles that rise up the page and fade,
 *      adding a lingering "embers" feel for ~3s.
 *   3) HALO — a soft expanding ring at the focal point, eased over 1.2s.
 *
 * Audio: synthesises a soft chime via Web Audio API (no asset needed) only
 * if the browser supports it. Falls back silently otherwise.
 */
export default function AngelSparks({ origin, sound = true, onDone, durationMs = 3500 }) {
    const audioCtxRef = useRef(null);

    // Compute focal point once per mount.
    const focal = useMemo(() => {
        if (origin && Number.isFinite(origin.x) && Number.isFinite(origin.y)) {
            return { x: origin.x, y: origin.y };
        }
        return {
            x: typeof window !== "undefined" ? window.innerWidth / 2 : 400,
            y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
        };
    }, [origin]);

    // Pre-randomise particle trajectories so re-renders don't reshuffle.
    const burst = useMemo(() => {
        const n = 28;
        return Array.from({ length: n }, (_, i) => {
            const angle = (Math.PI * 2 * i) / n + (Math.random() - 0.5) * 0.4;
            const distance = 90 + Math.random() * 110;
            return {
                id: `b${i}`,
                dx: Math.cos(angle) * distance,
                dy: Math.sin(angle) * distance,
                delay: Math.random() * 80,
                size: 3 + Math.random() * 4,
                rotate: Math.random() * 360,
            };
        });
    }, []);

    const drift = useMemo(() => {
        const n = 14;
        return Array.from({ length: n }, (_, i) => ({
            id: `d${i}`,
            // spread horizontally across a wide band, not just the focal point
            offsetX: -180 + Math.random() * 360,
            rise: 220 + Math.random() * 220,
            sway: -30 + Math.random() * 60,
            delay: 150 + Math.random() * 900,
            duration: 2400 + Math.random() * 1200,
            size: 2 + Math.random() * 3,
            opacity: 0.5 + Math.random() * 0.4,
        }));
    }, []);

    // Soft chime: a couple of overlapping sine partials with a quick attack
    // and gentle decay → "ting–sparkle". Triangle adds a brighter top.
    useEffect(() => {
        if (!sound) return;
        const Ctx = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
        if (!Ctx) return;
        const ac = new Ctx();
        audioCtxRef.current = ac;

        const t0 = ac.currentTime;
        const master = ac.createGain();
        master.gain.value = 0.22;
        master.connect(ac.destination);

        const tones = [
            { f: 1244.51, type: "sine", peak: 0.6, attack: 0.005, decay: 1.6 },     // D#6
            { f: 1864.66, type: "sine", peak: 0.45, attack: 0.005, decay: 1.4 },    // A#6
            { f: 2489.02, type: "triangle", peak: 0.3, attack: 0.01, decay: 1.0 },  // D#7 — bright shimmer
        ];

        tones.forEach((t) => {
            const osc = ac.createOscillator();
            osc.type = t.type;
            osc.frequency.value = t.f;
            const g = ac.createGain();
            g.gain.setValueAtTime(0, t0);
            g.gain.linearRampToValueAtTime(t.peak, t0 + t.attack);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + t.attack + t.decay);
            osc.connect(g);
            g.connect(master);
            osc.start(t0);
            osc.stop(t0 + t.attack + t.decay + 0.1);
        });

        const closeTimer = window.setTimeout(() => {
            try {
                ac.close();
            } catch {
                // already closed
            }
        }, 2500);
        return () => {
            window.clearTimeout(closeTimer);
            try {
                ac.close();
            } catch {
                // ignore
            }
        };
    }, [sound]);

    // Auto-dismiss
    useEffect(() => {
        const t = window.setTimeout(() => onDone && onDone(), durationMs);
        return () => window.clearTimeout(t);
    }, [durationMs, onDone]);

    return (
        <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[120] overflow-hidden"
            data-testid="angel-sparks"
        >
            {/* Page-wide brass glow — radial wash anchored to the focal point
                but spanning the entire viewport so the celebration is felt
                regardless of where the confirm button sits on the page (eg.
                below the fold on small Android screens). Swells in, breathes
                briefly, then fades. */}
            <span
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(circle at ${focal.x}px ${focal.y}px, rgba(255,210,140,0.55) 0%, rgba(207,160,82,0.30) 28%, rgba(207,160,82,0.08) 55%, rgba(207,160,82,0) 85%)`,
                    animation: "as-pageglow 1600ms ease-out forwards",
                    opacity: 0,
                }}
            />

            {/* Soft expanding halo */}
            <span
                className="absolute rounded-full"
                style={{
                    left: focal.x,
                    top: focal.y,
                    width: 24,
                    height: 24,
                    marginLeft: -12,
                    marginTop: -12,
                    background: "radial-gradient(circle, rgba(207,160,82,0.75) 0%, rgba(207,160,82,0) 65%)",
                    animation: "as-halo 1200ms ease-out forwards",
                }}
            />

            {/* Burst particles */}
            {burst.map((p) => (
                <span
                    key={p.id}
                    className="absolute rounded-full"
                    style={{
                        left: focal.x,
                        top: focal.y,
                        width: p.size,
                        height: p.size,
                        marginLeft: -p.size / 2,
                        marginTop: -p.size / 2,
                        background:
                            "radial-gradient(circle, rgba(255,222,158,1) 0%, rgba(207,160,82,0.85) 60%, rgba(207,160,82,0) 100%)",
                        boxShadow: "0 0 6px rgba(255,200,120,0.8)",
                        animation: `as-burst 1500ms cubic-bezier(0.16, 1, 0.3, 1) ${p.delay}ms forwards`,
                        // CSS variables consumed by the keyframe below
                        "--dx": `${p.dx}px`,
                        "--dy": `${p.dy}px`,
                        "--rot": `${p.rotate}deg`,
                    }}
                />
            ))}

            {/* Drifting motes */}
            {drift.map((d) => (
                <span
                    key={d.id}
                    className="absolute rounded-full"
                    style={{
                        left: focal.x + d.offsetX,
                        top: focal.y,
                        width: d.size,
                        height: d.size,
                        marginLeft: -d.size / 2,
                        marginTop: -d.size / 2,
                        background: "rgba(255,210,140,0.9)",
                        boxShadow: "0 0 4px rgba(255,200,120,0.7)",
                        animation: `as-drift ${d.duration}ms ease-out ${d.delay}ms forwards`,
                        opacity: 0,
                        "--rise": `-${d.rise}px`,
                        "--sway": `${d.sway}px`,
                        "--peak-opacity": d.opacity,
                    }}
                />
            ))}

            <style>{`
                @keyframes as-pageglow {
                    0%   { opacity: 0; }
                    25%  { opacity: 0.85; }
                    55%  { opacity: 0.55; }
                    100% { opacity: 0; }
                }
                @keyframes as-halo {
                    0%   { transform: scale(0.3); opacity: 0.9; }
                    60%  { opacity: 0.4; }
                    100% { transform: scale(14); opacity: 0; }
                }
                @keyframes as-burst {
                    0%   { transform: translate(0, 0) rotate(0deg) scale(0.4); opacity: 1; }
                    20%  { opacity: 1; }
                    100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.6); opacity: 0; }
                }
                @keyframes as-drift {
                    0%   { transform: translate(0, 0); opacity: 0; }
                    15%  { opacity: var(--peak-opacity); }
                    85%  { opacity: var(--peak-opacity); }
                    100% { transform: translate(var(--sway), var(--rise)); opacity: 0; }
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="angel-sparks"] * { animation: none !important; opacity: 0 !important; }
                }
            `}</style>
        </div>
    );
}
